import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { FaArrowLeft, FaArrowRight, FaSearchMinus, FaSearchPlus } from "react-icons/fa";
import {
  deleteEmployeePdf,
  fetchEmployeePdfs,
  uploadEmployeePdfs,
} from "../store/reducers/employeePdfSlice";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const getPdfName = (item) =>
  item?.name || item?.pdf_name || item?.title || item?.file_name || "PDF";

const getPdfUrl = (item) =>
  item?.pdf_file || item?.pdf || item?.file || item?.url || item?.path || "";

const useEmployeePdfAccess = () => {
  const organizationId = localStorage.getItem("organisation_id");
  const role = localStorage.getItem("user_role");
  const { organizations } = useSelector((state) => state.userOrganizations);
  const { organizationDetails } = useSelector((state) => state.organization);
  const normalizedRole = `${role || ""}`.toLowerCase();

  const isAdminOwnerFromList = organizations.some((org) => {
    const orgRole = `${org.role || ""}`.toLowerCase();
    return (
      org.organisation_id === +organizationId &&
      (orgRole === "admin" || orgRole === "owner")
    );
  });

  return {
    organizationId,
    featureEnabled: organizationDetails?.view_employee_pdfs,
    isAdminOwner:
      isAdminOwnerFromList ||
      normalizedRole === "admin" ||
      normalizedRole === "owner",
  };
};

// ─────────────────────────────────────────────
// Viewer (employee-facing)
// ─────────────────────────────────────────────
const EmployeePdfViewer = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const viewerRef = useRef(null);

  const { organizationId, featureEnabled } = useEmployeePdfAccess();
  const { pdfs } = useSelector((state) => state.employeePdfs);

  const [selectedPdfId, setSelectedPdfId] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(isMobile ? 0.92 : 1.2);
  const [fileBlob, setFileBlob] = useState(null);
  const [viewerWidth, setViewerWidth] = useState(0);

  const pdfList = useMemo(() => (Array.isArray(pdfs) ? pdfs : []), [pdfs]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchEmployeePdfs(organizationId));
    }
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!pdfList.length) {
      setSelectedPdfId(null);
      return;
    }
    const activePdf = pdfList.find((item) => item.id === selectedPdfId);
    if (!activePdf) {
      setSelectedPdfId(pdfList[0].id);
    }
  }, [pdfList, selectedPdfId]);

  const selectedIndex = useMemo(
    () => pdfList.findIndex((item) => item.id === selectedPdfId),
    [pdfList, selectedPdfId]
  );

  const selectedPdf = useMemo(
    () => (selectedIndex >= 0 ? pdfList[selectedIndex] : pdfList[0] || null),
    [pdfList, selectedIndex]
  );

  const rawUrl = getPdfUrl(selectedPdf);

  useEffect(() => {
    setNumPages(null);
    if (!rawUrl) {
      setFileBlob(null);
      return;
    }

    let objectUrl;
    const loadPdf = async () => {
      try {
        const res = await fetch(rawUrl, { cache: "no-store" });
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setFileBlob(objectUrl);
      } catch (err) {
        console.error("PDF load error", err);
        setFileBlob(null);
      }
    };

    loadPdf();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rawUrl]);

  useEffect(() => {
    const blockViewerEvent = (event) => {
      if (!viewerRef.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const blockShortcuts = (event) => {
      const isMetaPressed = event.ctrlKey || event.metaKey;
      const blockedKey = event.key?.toLowerCase();
      if (isMetaPressed && ["c", "s", "p"].includes(blockedKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("contextmenu", blockViewerEvent, true);
    document.addEventListener("copy", blockViewerEvent, true);
    document.addEventListener("cut", blockViewerEvent, true);
    document.addEventListener("dragstart", blockViewerEvent, true);
    document.addEventListener("selectstart", blockViewerEvent, true);
    window.addEventListener("keydown", blockShortcuts, true);

    return () => {
      document.removeEventListener("contextmenu", blockViewerEvent, true);
      document.removeEventListener("copy", blockViewerEvent, true);
      document.removeEventListener("cut", blockViewerEvent, true);
      document.removeEventListener("dragstart", blockViewerEvent, true);
      document.removeEventListener("selectstart", blockViewerEvent, true);
      window.removeEventListener("keydown", blockShortcuts, true);
    };
  }, []);

  const selectPdf = (index) => {
    const nextPdf = pdfList[index];
    if (!nextPdf) return;
    setSelectedPdfId(nextPdf.id);
    setZoom(isMobile ? 0.92 : 1.2);
  };

  useEffect(() => {
    const viewerElement = viewerRef.current;

    if (!viewerElement) return undefined;

    let animationFrameId = null;

    const updateViewerWidth = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const nextWidth = viewerElement.clientWidth;
        setViewerWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth,
        );
      });
    };

    updateViewerWidth();

    const resizeObserver = new ResizeObserver(updateViewerWidth);
    resizeObserver.observe(viewerElement);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      resizeObserver.disconnect();
    };
  }, [isMobile, fileBlob]);

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.2, isMobile ? 1.8 : 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.2, isMobile ? 0.7 : 0.6));
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const handleNextPdf = () => {
    if (selectedIndex < pdfList.length - 1) {
      selectPdf(selectedIndex + 1);
    }
  };

  const handlePrevPdf = () => {
    if (selectedIndex > 0) {
      selectPdf(selectedIndex - 1);
    }
  };

  if (featureEnabled === false) {
    return (
      <div className="p-3 text-center">
        Organisation does not have permission
      </div>
    );
  }

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <h1 className="mb-4 text-[24px] font-bold leading-tight text-black md:text-[30px]">
        {t("userMenuSidebar.label.EmployeePdfs")}
      </h1>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: isMobile ? "auto" : "calc(93vh - 72px)",
          minHeight: isMobile ? "100dvh" : undefined,
        }}
      >
      {/* ── Sidebar (desktop) ── */}
      {!isMobile && (
        <Box
          sx={{
            width: "28%",
            overflowY: "auto",
            borderRight: "1px solid #ddd",
            p: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            PDF List
          </Typography>

          {pdfList.length ? (
            pdfList.map((item, index) => (
              <Paper
                key={item.id}
                onClick={() => selectPdf(index)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: "pointer",
                  backgroundColor:
                    index === selectedIndex
                      ? "var(--org-bg-tint-1, #ffebee)"
                      : "#fff",
                  border:
                    index === selectedIndex
                      ? "1px solid #ef5350"
                      : "1px solid #eee",
                }}
              >
                <Typography variant="body2">
                  <strong>{getPdfName(item)}</strong>
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No PDFs found.
            </Typography>
          )}
        </Box>
      )}

      {/* ── Main content ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "72%",
          p: isMobile ? 1 : 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {/* Mobile: dropdown selector */}
        {isMobile && (
          <Box sx={{ mb: 1 }}>
            <select
              className="form-select"
              value={selectedIndex >= 0 ? selectedIndex : ""}
              onChange={(e) => selectPdf(Number(e.target.value))}
              disabled={!pdfList.length}
            >
              {!pdfList.length && <option value="">No PDFs available</option>}
              {pdfList.map((item, index) => (
                <option key={item.id} value={index}>
                  {getPdfName(item)}
                </option>
              ))}
            </select>
          </Box>
        )}

        {/* Toolbar */}
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: "6px",
            backgroundColor: "#f5f5f5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              fontSize: isMobile ? "0.85rem" : undefined,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedPdf ? getPdfName(selectedPdf) : "No PDF selected"}
          </Typography>
        </Box>

        {/* PDF canvas */}
        <Box
          ref={viewerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: "8px",
            p: isMobile ? 1 : 2,
            backgroundColor: "#fafafa",
            position: "relative",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            minHeight: isMobile ? "60vh" : undefined,
            "& canvas": {
              userSelect: "none",
              WebkitUserDrag: "none",
              maxWidth: "100%",
              height: "auto !important",
            },
            "& img": {
              pointerEvents: "none",
              userSelect: "none",
              WebkitUserDrag: "none",
            },
          }}
        >
          {fileBlob ? (
            <Document
              key={fileBlob}
              file={fileBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <span className="spinner-border spinner-border-sm"></span>
              }
            >
              {Array.from(new Array(numPages), (_, i) => (
                <Box key={i} mb={2} display="flex" justifyContent="center">
                  <Page
                    pageNumber={i + 1}
                    scale={isMobile ? undefined : zoom}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={
                      isMobile && viewerWidth
                        ? Math.max(220, Math.floor((viewerWidth - 16) * zoom))
                        : undefined
                    }
                  />
                </Box>
              ))}
            </Document>
          ) : (
            <Typography align="center" mt={5}>
              {pdfList.length ? "No PDF available" : "No PDFs available"}
            </Typography>
          )}

          {/* Zoom Controls — sticky bottom-right overlay inside viewer */}
          <Box
            sx={{
              position: "sticky",
              bottom: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-end",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                gap: 1,
                pointerEvents: "auto",
              }}
            >
              <button
                className="btn btn-light btn-sm shadow-sm"
                onClick={zoomIn}
                title="Zoom In"
                aria-label="Zoom in"
                style={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaSearchPlus size={14} />
              </button>
              <button
                className="btn btn-light btn-sm shadow-sm"
                onClick={zoomOut}
                title="Zoom Out"
                aria-label="Zoom out"
                style={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaSearchMinus size={14} />
              </button>
            </Box>
          </Box>
        </Box>

        {/* Navigation Bar */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "center" : "flex-end",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
            onClick={handlePrevPdf}
            disabled={selectedIndex <= 0}
            style={{
              width: 34,
              height: 34,
              padding: 0,
              borderRadius: "50%",
            }}
          >
            <FaArrowLeft size={13} />
          </button>

          <Typography
            variant="body2"
            sx={{
              minWidth: 90,
              textAlign: "center",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            PDF <strong>{selectedIndex >= 0 ? selectedIndex + 1 : 1}</strong>
            {" of "}
            <strong>{pdfList.length}</strong>
          </Typography>

          <button
            className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
            onClick={handleNextPdf}
            disabled={selectedIndex < 0 || selectedIndex === pdfList.length - 1}
            style={{
              width: 34,
              height: 34,
              padding: 0,
              borderRadius: "50%",
            }}
          >
            <FaArrowRight size={13} />
          </button>
        </Box>
      </Box>
      </Box>
    </div>
  );
};

// ─────────────────────────────────────────────
// Delete Confirmation Modal
// ─────────────────────────────────────────────
const DeletePdfModal = ({ show, onHide, onConfirm, deleting }) => (
  <Modal show={show} onHide={onHide} centered size="sm">
    <Modal.Body style={{ padding: "2rem 1.5rem 1.5rem", textAlign: "center" }}>
      {/* Icon */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#FEE2E2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1.1rem",
        }}
      >
        <i
          className="fas fa-trash-alt"
          style={{ fontSize: 22, color: "#DC2626" }}
        />
      </div>

      {/* Heading */}
      <h6
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: "#111827",
          marginBottom: 8,
        }}
      >
        Delete PDF?
      </h6>

      {/* Description */}
      <p
        style={{
          fontSize: 13,
          color: "#6B7280",
          marginBottom: "1.5rem",
          lineHeight: 1.5,
        }}
      >
        This PDF will be permanently removed and employees will no longer be
        able to access it. This action cannot be undone.
      </p>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onHide}
          disabled={deleting}
          style={{
            flex: 1,
            padding: "8px 0",
            borderRadius: 8,
            border: "1px solid #E5E7EB",
            background: "#fff",
            color: "#374151",
            fontWeight: 500,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          style={{
            flex: 1,
            padding: "8px 0",
            borderRadius: 8,
            border: "none",
            background: deleting ? "#FCA5A5" : "#DC2626",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            cursor: deleting ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            transition: "background 0.15s",
          }}
        >
          {deleting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                style={{ width: 12, height: 12, borderWidth: 2 }}
              />
              Deleting…
            </>
          ) : (
            <>
              <i className="fas fa-trash-alt" style={{ fontSize: 12 }} />
              Yes, Delete
            </>
          )}
        </button>
      </div>
    </Modal.Body>
  </Modal>
);

// ─────────────────────────────────────────────
// Management (admin-facing)
// ─────────────────────────────────────────────
export const EmployeePdfManagement = () => {
  const dispatch = useDispatch();
  const { organizationId, featureEnabled, isAdminOwner } =
    useEmployeePdfAccess();
  const { pdfs, loading, saving, deleting, total } = useSelector(
    (state) => state.employeePdfs
  );

  const itemsPerPage = 10;
  const MAX_SIZE_MB = 50;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const [fileErrors, setFileErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [pdfName, setPdfName] = useState("");
  // Single file only — stored as one File object or null
  const [selectedFile, setSelectedFile] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const pdfList = useMemo(() => (Array.isArray(pdfs) ? pdfs : []), [pdfs]);

  useEffect(() => {
    if (organizationId && featureEnabled && isAdminOwner) {
      dispatch(
        fetchEmployeePdfs({
          organizationId,
          management: true,
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
        })
      );
    }
  }, [
    currentPage,
    debouncedSearch,
    dispatch,
    featureEnabled,
    isAdminOwner,
    organizationId,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  if (featureEnabled === false || !isAdminOwner) {
    return (
      <div className="p-3 text-center">
        Organisation does not have permission
      </div>
    );
  }

  const openAddModal = () => {
    setPdfName("");
    setSelectedFile(null);
    setFileErrors({});
    setShowPdfModal(true);
  };

  // ── Single file handler ──
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";

    if (!files.length) return;

    const file = files[0]; // take only first

    if (file.type !== "application/pdf") {
      setFileErrors((prev) => ({
        ...prev,
        file: "Only PDF files are allowed.",
      }));
      return;
    }

    setSelectedFile(file);
    setFileErrors((prev) => ({ ...prev, file: null }));
  };

  // ── Drop handler (single file) ──
  const handleDrop = (e) => {
    e.preventDefault();
    handleFileChange({ target: { files: e.dataTransfer.files, value: "" } });
  };

  const handleSavePdf = async () => {
    const errors = {};
    if (!pdfName.trim()) errors.name = "PDF name is required.";
    if (!selectedFile) {
      errors.file = "Please select a PDF file.";
    } else if (selectedFile.size > MAX_SIZE_BYTES) {
      errors.file = "File exceeds the 50 MB size limit.";
    }
    if (Object.keys(errors).length) {
      setFileErrors(errors);
      return;
    }

    const formData = new FormData();
    formData.append("organization_id", organizationId);
    formData.append("name", pdfName.trim());
    formData.append("pdf_files", selectedFile);

    try {
      await dispatch(uploadEmployeePdfs(formData)).unwrap();
      toast.success("PDF uploaded successfully");
      setShowPdfModal(false);
      setFileErrors({});
      setCurrentPage(1);
      dispatch(
        fetchEmployeePdfs({
          organizationId,
          management: true,
          page: 1,
          limit: itemsPerPage,
          search: debouncedSearch,
        })
      );
    } catch (err) {
      toast.error(err?.error || "Upload failed");
    }
  };

  const fmtSize = (bytes) =>
    bytes >= 1024 * 1024
      ? (bytes / (1024 * 1024)).toFixed(1) + " MB"
      : Math.round(bytes / 1024) + " KB";

  const handleDeleteClick = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(
        deleteEmployeePdf({ id: selectedDeleteId, organizationId })
      ).unwrap();
      toast.success("PDF deleted successfully");
      setShowDeleteModal(false);
      dispatch(
        fetchEmployeePdfs({
          organizationId,
          management: true,
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
        })
      );
    } catch (error) {
      toast.error(error?.error || "Delete failed");
    }
  };

  const pageCount = Math.ceil((Number(total) || 0) / itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const isOverSize = selectedFile && selectedFile.size > MAX_SIZE_BYTES;

  return (
    <>
      <div className="row my-2 align-items-center">
        <div className="col-12 d-flex align-items-center justify-content-between gap-2">
          {/* Title */}
          <div className="fw-bold" style={{ whiteSpace: "nowrap" }}>
            Employee PDF Section
          </div>

          {/* Search + Clear */}
          <div
            className="d-flex align-items-center"
            style={{
              width: "260px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              backgroundColor: "#fff",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              className="form-control border-0 shadow-none"
              placeholder="Search PDF"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ backgroundColor: "transparent" }}
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                  setCurrentPage(1);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: "0 10px",
                  cursor: "pointer",
                  color: "#999",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Add Button */}
          <button
            className="btn"
            type="button"
            onClick={openAddModal}
            style={{
              whiteSpace: "nowrap",
              backgroundColor: "#e06060",
              color: "#fff",
              borderRadius: "6px",
              border: "none",
              padding: "6px 16px",
              fontWeight: 500,
              fontSize: "0.875rem",
            }}
          >
            Add new PDF
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: "80px" }}>Sr. No</th>
              <th style={{ textAlign: "center" }}>PDF Name</th>
              <th style={{ width: "80px", textAlign: "center" }}>Remove</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="text-center py-3">
                  Loading...
                </td>
              </tr>
            ) : pdfList.length ? (
              pdfList.map((item, i) => (
                <tr key={item.id}>
                  <td>{i + 1 + itemsPerPage * (currentPage - 1)}</td>
                  <td
                    style={{
                      textAlign: "center",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getPdfName(item)}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm bg_color"
                      onClick={() => handleDeleteClick(item.id)}
                      aria-label="Delete PDF"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center py-3">
                  Data Not Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pageCount > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            nextLabel="Next"
            previousLabel="Previous"
            onPageChange={handlePageClick}
            pageCount={pageCount}
            forcePage={currentPage - 1}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            containerClassName="pagination flex-wrap"
            activeClassName="active"
          />
        </div>
      )}

      {/* ── Add PDF Modal ── */}
      <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} centered>
        <Modal.Header
          closeButton
          style={{
            borderBottom: "0.5px solid var(--bs-border-color, #dee2e6)",
            padding: "1rem 1.25rem",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: "#FAECE7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-file-upload"
                style={{ color: "#993C1D", fontSize: 16 }}
              />
            </div>
            <div>
              <Modal.Title
                style={{ fontSize: 15, fontWeight: 500, marginBottom: 1 }}
              >
                Add new PDF
              </Modal.Title>
              <div style={{ fontSize: 12, color: "#6c757d" }}>
                Upload a document for employees
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.1rem",
          }}
        >
          {/* PDF Name */}
          <div>
            <label
              className="form-label"
              style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
            >
              PDF name <span style={{ color: "#D85A30" }}>*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              className={`form-control form-control-sm ${
                fileErrors.name ? "is-invalid" : ""
              }`}
              placeholder="e.g. Employee Handbook 2025"
              value={pdfName}
              onChange={(e) => {
                setPdfName(e.target.value);
                if (e.target.value.trim())
                  setFileErrors((p) => ({ ...p, name: null }));
              }}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              {fileErrors.name ? (
                <div className="text-danger" style={{ fontSize: 11 }}>
                  <i className="fas fa-exclamation-circle me-1" />
                  {fileErrors.name}
                </div>
              ) : (
                <span />
              )}
              <span style={{ fontSize: 11, color: "#adb5bd" }}>
                {pdfName.length} / 100
              </span>
            </div>
          </div>

          {/* Drop zone — single file */}
          <div>
            <label
              className="form-label"
              style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
            >
              PDF file <span style={{ color: "#D85A30" }}>*</span>
            </label>

            {/* Show drop-zone only when no file is selected yet */}
            {!selectedFile && (
              <label
                style={{
                  display: "block",
                  border: `1.5px dashed ${
                    fileErrors.file ? "#E24B4A" : "#ced4da"
                  }`,
                  borderRadius: 8,
                  padding: "1.5rem 1rem",
                  textAlign: "center",
                  cursor: "pointer",
                  background: fileErrors.file ? "#FCEBEB" : "transparent",
                  transition: "border-color .15s",
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#f8f9fa",
                    border: "0.5px solid #dee2e6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 10px",
                  }}
                >
                  <i
                    className="fas fa-cloud-upload-alt"
                    style={{ fontSize: 18, color: "#6c757d" }}
                  />
                </div>
                <div style={{ fontSize: 13, color: "#6c757d" }}>
                  Drag & drop or{" "}
                  <span style={{ color: "#D85A30", fontWeight: 500 }}>
                    browse file
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 4 }}>
                  PDF only · Max 50 MB
                </div>
              </label>
            )}

            {fileErrors.file && (
              <div
                style={{
                  fontSize: 11,
                  color: "#E24B4A",
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className="fas fa-exclamation-circle" /> {fileErrors.file}
              </div>
            )}

            {/* Selected file card */}
            {selectedFile && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `0.5px solid ${isOverSize ? "#F09595" : "#dee2e6"}`,
                  background: isOverSize ? "#FCEBEB" : "#f8f9fa",
                  marginTop: 4,
                }}
              >
                {/* File icon */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: isOverSize ? "#F7C1C1" : "#FAECE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="fas fa-file-pdf"
                    style={{
                      fontSize: 15,
                      color: isOverSize ? "#A32D2D" : "#993C1D",
                    }}
                  />
                </div>

                {/* Name + size */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#111827",
                    }}
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: isOverSize ? "#E24B4A" : "#adb5bd",
                      marginTop: 1,
                    }}
                  >
                    {fmtSize(selectedFile.size)}
                    {isOverSize ? " · exceeds 50 MB limit" : ""}
                  </div>
                </div>

                {/* Replace link */}
                <label
                  style={{
                    fontSize: 11,
                    color: "#D85A30",
                    fontWeight: 500,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                  title="Replace file"
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  Replace
                </label>

                {/* Remove button */}
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setFileErrors((p) => ({ ...p, file: null }));
                  }}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#adb5bd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  aria-label="Remove file"
                >
                  <i className="fas fa-times" style={{ fontSize: 12 }} />
                </button>
              </div>
            )}

            {/* Over-size warning */}
            {isOverSize && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  marginTop: 8,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "#FCEBEB",
                  border: "0.5px solid #F09595",
                  fontSize: 12,
                  color: "#A32D2D",
                }}
              >
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ marginTop: 1 }}
                />
                <span>
                  This file exceeds the 50 MB limit. Please choose a smaller
                  file to continue.
                </span>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "0.5px solid var(--bs-border-color, #dee2e6)",
            padding: "1rem 1.25rem",
          }}
        >
          <Button
            variant="light"
            onClick={() => setShowPdfModal(false)}
            style={{ fontSize: 13 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePdf}
            disabled={saving}
            style={{
              background: "#D85A30",
              border: "none",
              fontSize: 13,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="fas fa-upload" />
            {saving ? "Saving..." : "Save PDF"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <DeletePdfModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        deleting={!!deleting}
      />
    </>
  );
};

export default EmployeePdfViewer;
